import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getPlayerByID } from "../../services/playersAPI";
import { Modal } from "../../components/modals/Modal";
import { CreatePlayer } from "../../components/admin/CreatePlayer";
import Loader from "../../components/ui/loader";
import { formatDate } from "../../utils/dateAndTime";
import { useAuthStore } from "../../store/authStore";
import playerSilhouette from "../../assets/player-silhoutte.png";

export const Player = () => {
  const { playerID } = useParams();
  const [player, setPlayer] = useState(null);
  const [isOpenModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const { user } = useAuthStore();

  const fetchPlayer = async () => {
    setIsLoading(true);
    try {
      let response = await getPlayerByID(playerID);
      if (response.success) {
        setPlayer(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    fetchPlayer();
  }, [playerID]); //eslint-disable-line

  useEffect(() => {
    setHasImageError(false);
  }, [playerID, player?.player?.playerImageFile]);

  // Group fights by season
  const fightsBySeason = {};
  if (player && player.fights) {
    player.fights.forEach((fight) => {
      const season = fight.SeasonDetails.season;
      if (!fightsBySeason[season]) {
        fightsBySeason[season] = [];
      }
      fightsBySeason[season].push(fight);
    });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-flyers-black flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-flyers-black text-white">
      {player && (
        <div className="max-w-6xl mx-auto">
          {/* Profile Hero Card */}
          <div className="bg-flyers-charcoal border border-flyers-dark-gray rounded-lg p-6">
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              {/* Player Image / Fallback */}
              <div className="flex-shrink-0">
                {player?.player?.playerImageFile && !hasImageError ? (
                  <img
                    src={player.player.playerImageFile}
                    alt={`${player.player.firstName} ${player.player.lastName}`}
                    className="w-48 h-48 md:w-56 md:h-56 object-cover rounded-lg border-2 border-flyers-dark-gray"
                    onError={() => setHasImageError(true)}
                  />
                ) : (
                  <img
                    src={playerSilhouette}
                    alt="Player silhouette placeholder"
                    className="w-48 h-48 md:w-56 md:h-56 object-contain bg-flyers-black rounded-lg border-2 border-flyers-dark-gray p-2"
                  />
                )}
              </div>

              {/* Player Details */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="font-heading text-2xl md:text-3xl font-black uppercase tracking-wide text-flyers-orange">
                  {player?.player?.firstName} {player?.player?.lastName}
                </h1>

                {player?.player?.nickname && player.player.nickname !== "" && (
                  <p className="text-flyers-mid-gray italic text-lg mt-1">
                    &ldquo;{player.player.nickname}&rdquo;
                  </p>
                )}

                <div className="w-16 h-0.5 bg-flyers-orange mt-3 mb-4 mx-auto md:mx-0" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                  {player?.player?.position && player.player.position !== "" && (
                    <div>
                      <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Position</span>
                      <p className="text-white font-semibold">{player.player.position}</p>
                    </div>
                  )}
                  {player?.player?.height && player.player.height !== "N/A" && (
                    <div>
                      <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Height</span>
                      <p className="text-white font-semibold">{player.player.height}</p>
                    </div>
                  )}
                  {player?.player?.weight && player.player.weight !== "N/A" && (
                    <div>
                      <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Weight</span>
                      <p className="text-white font-semibold">{player.player.weight}</p>
                    </div>
                  )}
                  {player?.player?.shoots && player.player.shoots !== "N/A" && (
                    <div>
                      <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Shoots</span>
                      <p className="text-white font-semibold">{player.player.shoots}</p>
                    </div>
                  )}
                </div>

                {user?.role === "admin" && (
                  <div className="mt-4">
                    <Button
                      onClick={() => setOpenModal(true)}
                      label="Update Player"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Career Record */}
          <div className="mt-6 px-6">
            <span className="font-heading font-bold uppercase tracking-wider text-flyers-light-gray">Career Record</span>
            <div className="flex flex-wrap gap-6 mt-3">
              <span className="bg-flyers-charcoal border border-flyers-dark-gray rounded px-4 py-2 text-sm">
                Total Fights: <span className="text-flyers-orange font-bold">{player?.fights?.length || 0}</span>
              </span>
              <span className="bg-flyers-charcoal border border-flyers-dark-gray rounded px-4 py-2 text-sm">
                Wins: <span className="text-green-500 font-bold">{player?.player?.wins}</span>
              </span>
              <span className="bg-flyers-charcoal border border-flyers-dark-gray rounded px-4 py-2 text-sm">
                Losses: <span className="text-red-500 font-bold">{player?.player?.losses}</span>
              </span>
              <span className="bg-flyers-charcoal border border-flyers-dark-gray rounded px-4 py-2 text-sm">
                Draws: <span className="text-flyers-mid-gray font-bold">{player?.player?.draws}</span>
              </span>
            </div>
          </div>

          {player?.fights ? (
            <div className="mt-6 space-y-8">
              {Object.entries(fightsBySeason).map(([season, fights]) => (
                <div key={season}>
                  <h2 className="font-heading text-xl font-bold uppercase tracking-wider text-flyers-orange mb-4">
                    {season} Season
                  </h2>
                  <div className="overflow-x-auto rounded-lg border border-flyers-dark-gray">
                    <table className="min-w-full leading-normal bg-flyers-black">
                      <thead>
                        <tr>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">ID</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Date</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Game</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Fight Type</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Opponent</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">League</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Home Team</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Away Team</th>
                          <th className="bg-flyers-charcoal text-flyers-orange font-heading font-bold uppercase tracking-wider text-sm py-3 px-4 text-start border-b-2 border-flyers-orange">Stats</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fights.map((fight) => {
                          const isPlayer1 = fight?.Player1Details?.PlayerID === Number(playerID);
                          const opponentPlayer = isPlayer1 ? fight?.Player2Details : fight?.Player1Details;
                          const youVotes = isPlayer1 ? fight?.stats?.player1Votes : fight?.stats?.player2Votes;
                          const opponentVotes = isPlayer1 ? fight?.stats?.player2Votes : fight?.stats?.player1Votes;

                          return (
                            <tr key={fight?.FightID} className="border-b border-flyers-dark-gray hover:bg-flyers-charcoal transition-colors">
                              <td className="py-3 px-4 text-sm">
                                <Link to={`/game/${fight?.GameDetails?.GameID}`} className="text-flyers-orange hover:text-flyers-orange-light">
                                  {fight?.FightID}
                                </Link>
                              </td>
                              <td className="py-3 px-4 text-sm text-flyers-light-gray">{formatDate(fight?.date)}</td>
                              <td className="py-3 px-4 text-sm">
                                <Link to={`/game/${fight?.GameDetails?.GameID}`} className="text-flyers-orange hover:text-flyers-orange-light">
                                  {fight?.GameDetails?.gameType}
                                </Link>
                              </td>
                              <td className="py-3 px-4 text-sm text-flyers-light-gray">{fight?.fightType}</td>
                              <td className="py-3 px-4 text-sm">
                                <Link to={`/player/${opponentPlayer?.PlayerID}`} className="text-flyers-orange hover:text-flyers-orange-light">
                                  {opponentPlayer?.firstName} {opponentPlayer?.lastName}
                                </Link>
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <Link to={`/league/${fight?.LeagueDetails?.LeagueID}`} className="text-flyers-orange hover:text-flyers-orange-light">
                                  {fight?.LeagueDetails?.name}
                                </Link>
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <Link to={`/team/${fight?.GameDetails?.HomeTeamDetails?.TeamID}`} className="text-flyers-orange hover:text-flyers-orange-light">
                                  {fight?.GameDetails?.HomeTeamDetails?.name}
                                </Link>
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <Link to={`/team/${fight?.GameDetails?.AwayTeamDetails?.TeamID}`} className="text-flyers-orange hover:text-flyers-orange-light">
                                  {fight?.GameDetails?.AwayTeamDetails?.name}
                                </Link>
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <Link to={`/fight/${fight?.FightID}`} className="flex flex-col space-y-1 text-flyers-light-gray hover:text-white">
                                  <span>Votes: {fight?.stats?.totalVotes}</span>
                                  <span>You: {youVotes} | Opp: {opponentVotes}</span>
                                  <span>Comments: {fight?.stats?.totalComments}</span>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-8">
              <h1 className="text-lg text-flyers-mid-gray">No fights found for this player.</h1>
            </div>
          )}
        </div>
      )}

      {isOpenModal && (
        <Modal isOpen={isOpenModal} close={handleCloseModal}>
          <CreatePlayer
            id={playerID}
            player={player?.player}
            handleClose={handleCloseModal}
            fetchPlayer={fetchPlayer}
          />
        </Modal>
      )}
    </div>
  );
};

// Inline admin button used only here
function Button({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 bg-flyers-orange text-white font-heading font-bold uppercase tracking-wider rounded hover:bg-flyers-orange-light transition-colors duration-150"
    >
      {label}
    </button>
  );
}
